import React, { useState, useCallback } from 'react';
import { ArrowLeft, Plus, Edit2, Trash2, Search, Folder, Calendar, Building2, Users } from 'lucide-react';
import { Proyecto, ProyectoCreate, ProyectoUpdate } from '../../types';
import ApiService from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';
import MultiSelect from '../MultiSelect';

interface ProyectosConfigProps {
  onBack: () => void;
}

interface ProyectoFormData {
  nombre: string;
  descripcion: string;
  fechaInicio: string;
  fechaFin: string;
  clienteEmpresa: string;
  estado: 'Planificación' | 'En Progreso' | 'Completado';
  areaId: number;
  servicioIds: number[];
}

const ProyectosConfig: React.FC<ProyectosConfigProps> = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProyecto, setEditingProyecto] = useState<Proyecto | null>(null);
  const [formData, setFormData] = useState<ProyectoFormData>({
    nombre: '',
    descripcion: '',
    fechaInicio: new Date().toISOString().split('T')[0],
    fechaFin: '',
    clienteEmpresa: '',
    estado: 'Planificación',
    areaId: 0,
    servicioIds: []
  });

  // Hooks para datos
  const fetchProyectos = useCallback(() => ApiService.getProyectos(), []);
  const fetchAreas = useCallback(() => ApiService.getAreas(), []);
  const fetchServicios = useCallback(() => ApiService.getServicios(), []);

  const { data: proyectos, loading, error, refetch } = useApi(fetchProyectos);
  const { data: areas } = useApi(fetchAreas);
  const { data: servicios } = useApi(fetchServicios);

  const { execute: createProyecto, loading: creating } = useAsyncOperation();
  const { execute: updateProyecto, loading: updating } = useAsyncOperation();
  const { execute: deleteProyecto } = useAsyncOperation();

  // Filtrar proyectos por búsqueda
  const filteredProyectos = proyectos?.filter(proyecto =>
    proyecto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (proyecto.clienteEmpresa && proyecto.clienteEmpresa.toLowerCase().includes(searchTerm.toLowerCase())) ||
    proyecto.area.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proyecto.estado.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Manejar apertura de modal
  const handleOpenModal = (proyecto?: Proyecto) => {
    if (proyecto) {
      setEditingProyecto(proyecto);
      setFormData({
        nombre: proyecto.nombre,
        descripcion: proyecto.descripcion || '',
        fechaInicio: proyecto.fechaInicio.split('T')[0],
        fechaFin: proyecto.fechaFin ? proyecto.fechaFin.split('T')[0] : '',
        clienteEmpresa: proyecto.clienteEmpresa || '',
        estado: proyecto.estado,
        areaId: proyecto.area.id,
        servicioIds: proyecto.servicios.map(ps => ps.servicio.id)
      });
    } else {
      setEditingProyecto(null);
      setFormData({
        nombre: '',
        descripcion: '',
        fechaInicio: new Date().toISOString().split('T')[0],
        fechaFin: '',
        clienteEmpresa: '',
        estado: 'Planificación',
        areaId: areas?.[0]?.id || 0,
        servicioIds: []
      });
    }
    setIsModalOpen(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProyecto(null);
    setFormData({
      nombre: '',
      descripcion: '',
      fechaInicio: new Date().toISOString().split('T')[0],
      fechaFin: '',
      clienteEmpresa: '',
      estado: 'Planificación',
      areaId: 0,
      servicioIds: []
    });
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nombre.trim() || !formData.areaId) {
      alert('El nombre y el área son obligatorios');
      return;
    }

    if (formData.fechaFin && formData.fechaFin < formData.fechaInicio) {
      alert('La fecha de fin debe ser mayor o igual a la fecha de inicio');
      return;
    }

    try {
      const submitData: ProyectoCreate | ProyectoUpdate = {
        nombre: formData.nombre,
        descripcion: formData.descripcion || undefined,
        fechaInicio: formData.fechaInicio,
        fechaFin: formData.fechaFin || undefined,
        clienteEmpresa: formData.clienteEmpresa || undefined,
        estado: formData.estado,
        areaId: formData.areaId,
        servicioIds: formData.servicioIds.length > 0 ? formData.servicioIds : undefined,
        ...(editingProyecto && { activo: true })
      };

      if (editingProyecto) {
        await updateProyecto(() =>
          ApiService.updateProyecto(editingProyecto.id, submitData as ProyectoUpdate)
        );
      } else {
        await createProyecto(() => ApiService.createProyecto(submitData as ProyectoCreate));
      }

      handleCloseModal();
      refetch();
    } catch (error) {
      console.error('Error al guardar proyecto:', error);
    }
  };

  // Eliminar proyecto
  const handleDelete = async (id: number, nombre: string) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el proyecto "${nombre}"?`)) {
      return;
    }

    try {
      await deleteProyecto(() => ApiService.deleteProyecto(id));
      refetch();
    } catch (error) {
      console.error('Error al eliminar proyecto:', error);
    }
  };

  // Función para obtener color del estado
  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'Planificación':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
      case 'En Progreso':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
      case 'Completado':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300';
    }
  };

  // Formatear fecha
  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </button>
          <div className="flex items-center space-x-2">
            <Folder className="w-6 h-6 text-purple-600" />
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">Gestión de Proyectos</h1>
          </div>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center space-x-2 btn-primary transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nuevo Proyecto</span>
        </button>
      </div>

      {/* Búsqueda */}
      <div className="card p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar proyectos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50"
          />
        </div>
      </div>

      {/* Lista de proyectos */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Proyecto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Área
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Fechas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Servicios
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-700">
              {filteredProyectos.map((proyecto) => (
                <tr key={proyecto.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                  <td className="px-6 py-4">
                    <div className="font-medium text-neutral-900 dark:text-neutral-50">{proyecto.nombre}</div>
                    {proyecto.descripcion && (
                      <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate max-w-xs">
                        {proyecto.descripcion}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {proyecto.area.color && (
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: proyecto.area.color }}
                        />
                      )}
                      <span className="text-sm text-neutral-900 dark:text-neutral-50">
                        {proyecto.area.nombre}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-neutral-900 dark:text-neutral-50">
                      {proyecto.clienteEmpresa ? (
                        <>
                          <Building2 className="w-4 h-4 mr-2 text-neutral-400" />
                          {proyecto.clienteEmpresa}
                        </>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-neutral-900 dark:text-neutral-50">
                      <Calendar className="w-4 h-4 mr-2 text-neutral-400" />
                      <div>
                        <div>{formatFecha(proyecto.fechaInicio)}</div>
                        {proyecto.fechaFin && (
                          <div className="text-xs text-neutral-500">→ {formatFecha(proyecto.fechaFin)}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(proyecto.estado)}`}>
                      {proyecto.estado}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4 text-neutral-400" />
                      <span className="text-sm text-neutral-900 dark:text-neutral-50">
                        {proyecto.servicios.length} servicio{proyecto.servicios.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {proyecto.servicios.length > 0 && (
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        {proyecto.servicios.slice(0, 2).map(ps => ps.servicio.tecnologia).join(', ')}
                        {proyecto.servicios.length > 2 && ` +${proyecto.servicios.length - 2}`}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleOpenModal(proyecto)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(proyecto.id, proyecto.nombre)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredProyectos.length === 0 && (
            <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
              {searchTerm ? 'No se encontraron proyectos que coincidan con la búsqueda' : 'No hay proyectos registrados'}
            </div>
          )}
        </div>
      </div>

      {/* Modal de crear/editar */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6 text-neutral-900 dark:text-neutral-50">
                {editingProyecto ? 'Editar Proyecto' : 'Nuevo Proyecto'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50"
                    required
                  />
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50"
                  />
                </div>

                {/* Área */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Área *
                  </label>
                  <select
                    value={formData.areaId}
                    onChange={(e) => setFormData({ ...formData, areaId: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50"
                    required
                  >
                    <option value={0}>Seleccionar área</option>
                    {areas?.map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Cliente */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Cliente/Empresa
                  </label>
                  <input
                    type="text"
                    value={formData.clienteEmpresa}
                    onChange={(e) => setFormData({ ...formData, clienteEmpresa: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50"
                  />
                </div>

                {/* Fechas */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Fecha Inicio *
                    </label>
                    <input
                      type="date"
                      value={formData.fechaInicio}
                      onChange={(e) => setFormData({ ...formData, fechaInicio: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Fecha Fin
                    </label>
                    <input
                      type="date"
                      value={formData.fechaFin}
                      onChange={(e) => setFormData({ ...formData, fechaFin: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50"
                    />
                  </div>
                </div>

                {/* Estado */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Estado *
                  </label>
                  <select
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value as 'Planificación' | 'En Progreso' | 'Completado' })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50"
                    required
                  >
                    <option value="Planificación">Planificación</option>
                    <option value="En Progreso">En Progreso</option>
                    <option value="Completado">Completado</option>
                  </select>
                </div>

                {/* Servicios */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Servicios Utilizados
                  </label>
                  {servicios && (
                    <MultiSelect
                      options={servicios.map(s => ({ id: s.id, nombre: s.tecnologia }))}
                      selectedIds={formData.servicioIds}
                      onChange={(ids) => setFormData({ ...formData, servicioIds: ids })}
                      placeholder="Seleccionar servicios..."
                    />
                  )}
                </div>

                {/* Botones */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creating || updating}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {creating || updating ? 'Guardando...' : editingProyecto ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProyectosConfig;
